/**
 * CatalogoProdutos.jsx - Scriptus Pharma Partner
 * Página de Catálogo de Produtos - Redesenhada
 * 
 * ✅ Design alinhado com Dashboard.jsx
 * ✅ Layout compacto sem sidebar
 * ✅ Fontes menores e interface mais densa
 * ✅ Glassmorphism Premium Refinado
 * ✅ Botão Gerenciar Estoque único e horizontal
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCatalogo } from '../hooks/useCatalogo';
import HeaderMenu from '../components/HeaderMenu';
import ModalCadastrarProduto from '../components/ModalCadastrarProduto';
import CatalogoEstoque from './CatalogoEstoque';
import {
  Plus,
  Package,
  Pill,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Store,
  Crown,
  Archive,
  TrendingUp,
  Eye,
  Users,
  ArrowRight,
  X,
  Sparkles
} from 'lucide-react';

// ============================================================================
// Componente: GlassCard (Reutilizado do Dashboard)
// ============================================================================
function GlassCard({ children, className = '', elevated = false }) {
  return (
    <div className={`${elevated ? 'glass-card-elevated-refined' : 'glass-card-refined'} rounded-2xl ${className}`}>
      {children}
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function CatalogoProdutos() {
  const { user, isPartner } = useAuth();
  const { 
    criarProduto, 
    estatisticas, 
    dadosFarmacia,
    loading,
    error,
    clearError
  } = useCatalogo();

  // ============================================================================
  // ESTADOS
  // ============================================================================
  const [modalAberto, setModalAberto] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mensagem, setMensagem] = useState({ texto: '', tipo: 'success' });
  const [mostrarEstoque, setMostrarEstoque] = useState(false);

  // ============================================================================
  // REFS
  // ============================================================================
  const mountedRef = useRef(true);
  const mensagemTimeoutRef = useRef(null);

  // ============================================================================
  // CLEANUP
  // ============================================================================
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (mensagemTimeoutRef.current) {
        clearTimeout(mensagemTimeoutRef.current);
      }
    };
  }, []);

  // ============================================================================
  // SCROLL DETECTION
  // ============================================================================
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (mountedRef.current) {
            setScrolled(window.scrollY > 10);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ============================================================================
  // HELPERS
  // ============================================================================
  const mostrarMensagem = useCallback((texto, tipo = 'success', duracao = 4000) => {
    if (!mountedRef.current) return;
    
    if (mensagemTimeoutRef.current) {
      clearTimeout(mensagemTimeoutRef.current);
    }
    
    setMensagem({ texto, tipo });
    
    mensagemTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        setMensagem({ texto: '', tipo: 'success' });
      }
    }, duracao);
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleSalvarProduto = useCallback(async (dados) => {
    try {
      await criarProduto(dados);
      setModalAberto(false);
      mostrarMensagem('Produto cadastrado com sucesso!', 'success');
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao cadastrar produto', 'error');
    }
  }, [criarProduto, mostrarMensagem]);

  // ============================================================================
  // VERIFICAÇÃO DE PLANO
  // ============================================================================
  if (!isPartner) {
    return (
      <div className="min-h-screen bg-gradient-refined flex items-center justify-center p-4 relative">
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="background-pattern-refined"></div>
          <div className="background-glow-1"></div>
          <div className="background-glow-2"></div>
        </div>

        <GlassCard elevated className="p-8 max-w-md text-center relative z-10">
          <div className="main-icon-refined mx-auto mb-4 bg-gradient-to-br from-purple-100 to-purple-50 border-2 border-purple-200/50">
            <Crown className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="sf-pro-display font-bold text-slate-900 text-xl mb-2">
            Recurso Exclusivo Partner
          </h2>
          <p className="sf-pro-text text-slate-600 text-sm mb-6 leading-relaxed">
            O Catálogo de Produtos está disponível apenas para farmácias do plano Partner. 
            Faça upgrade para aparecer nas prescrições médicas!
          </p>
          <Link
            to="/assinatura?upgrade=partner"
            className="button-primary-refined inline-flex"
          >
            <Crown className="w-4 h-4" />
            <span>Fazer Upgrade</span>
          </Link>
        </GlassCard>
        <style>{styles}</style>
      </div>
    );
  }

  // ============================================================================
  // RENDERIZAÇÃO CONDICIONAL - ESTOQUE
  // ============================================================================
  if (mostrarEstoque) {
    return <CatalogoEstoque onVoltar={() => setMostrarEstoque(false)} />;
  }

  // ============================================================================
  // RENDERIZAÇÃO PRINCIPAL
  // ============================================================================
  return (
    <div className="min-h-screen bg-gradient-refined relative overflow-x-hidden">
      
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="background-pattern-refined"></div>
        <div className="background-glow-1"></div>
        <div className="background-glow-2"></div>
        <div className="background-glow-3"></div>
      </div>

      {/* Overlay para modal */}
      {modalAberto && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30"
          onClick={() => setModalAberto(false)}
        />
      )}

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'header-scrolled-refined' : 'header-refined'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="logo-link-refined">
              <img src="/logo.PNG" alt="Scriptus RxIA" className="h-5 w-auto" />
            </Link>
            <HeaderMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10">
        
        {/* Título da Página */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="badge-pharmacy-refined mb-2 inline-flex">
            <Store className="w-3.5 h-3.5 text-emerald-600" />
            <span className="sf-pro-text text-xs font-semibold text-emerald-700">
              {dadosFarmacia.nome}
            </span>
          </div>
          <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-slate-900 tracking-tight mb-1.5">
            Catálogo de Produtos
          </h1>
          <p className="text-xs sm:text-sm sf-pro-text text-slate-500 font-normal">
            Cadastre produtos para aparecer nas prescrições médicas
          </p>
        </div>

        {/* Card Principal - Adicionar Produto */}
        <GlassCard elevated className="p-5 sm:p-6 mb-6 shadow-xl">
          <div className="space-y-4">
            {/* Header do Card */}
            <div className="flex items-start gap-3">
              <div className="main-icon-refined bg-gradient-to-br from-emerald-100 to-emerald-50 border-2 border-emerald-200/50">
                <Pill className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h2 className="sf-pro-display font-semibold text-slate-900 text-lg mb-1">
                  Adicionar Novo Produto
                </h2>
                <p className="sf-pro-text text-slate-600 text-xs leading-relaxed">
                  Cadastre medicamentos com preço e disponibilidade. Seus produtos serão 
                  exibidos para médicos durante a prescrição.
                </p>
              </div>
            </div>

            {/* Botão de Cadastrar */}
            <button
              onClick={() => setModalAberto(true)}
              disabled={loading.salvando}
              className="button-primary-refined-full"
            >
              {loading.salvando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="sf-pro-text font-semibold">Aguarde...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span className="sf-pro-text font-semibold">Cadastrar Produto</span>
                </>
              )}
            </button>
          </div>
        </GlassCard>

        {/* Container de Estatísticas e Ações */}
        <GlassCard elevated className="p-5 sm:p-6 shadow-xl">
          {/* Grid de Estatísticas */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="stat-card-compact">
              <div className="stat-icon-compact bg-gradient-to-br from-blue-500 to-blue-600">
                <Package className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="sf-pro-display font-bold text-slate-900 text-base">
                  {estatisticas.total}
                </p>
                <p className="sf-pro-text text-slate-500 text-xs">Produtos</p>
              </div>
            </div>

            <div className="stat-card-compact">
              <div className="stat-icon-compact bg-gradient-to-br from-emerald-500 to-emerald-600">
                <CheckCircle className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="sf-pro-display font-bold text-emerald-600 text-base">
                  {estatisticas.disponiveis}
                </p>
                <p className="sf-pro-text text-slate-500 text-xs">Disponíveis</p>
              </div>
            </div>

            <div className="stat-card-compact">
              <div className="stat-icon-compact bg-gradient-to-br from-purple-500 to-purple-600">
                <Eye className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="sf-pro-display font-bold text-purple-600 text-base">
                  {estatisticas.totalVisualizacoes}
                </p>
                <p className="sf-pro-text text-slate-500 text-xs">Visualizações</p>
              </div>
            </div>

            <div className="stat-card-compact">
              <div className="stat-icon-compact bg-gradient-to-br from-amber-500 to-amber-600">
                <TrendingUp className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="sf-pro-display font-bold text-amber-600 text-base">
                  {estatisticas.totalLeads}
                </p>
                <p className="sf-pro-text text-slate-500 text-xs">Leads</p>
              </div>
            </div>
          </div>

          {/* Seção de Ações */}
          <div className="border-t border-slate-200/50 pt-5">
            <div className="flex-1 text-center sm:text-left mb-6">
              <h3 className="sf-pro-display font-semibold text-slate-800 text-sm mb-2 flex items-center justify-center sm:justify-start gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                Como Gerar Leads
              </h3>
              <ul className="sf-pro-text text-xs text-slate-600 space-y-1 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  <span>Médicos veem seus produtos ao prescrever</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  <span>Pacientes recebem indicação no QR Code</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  <span>Leads chegam direto no WhatsApp!</span>
                </li>
              </ul>
            </div>
            
            {/* Botão Gerenciar Estoque - Horizontal */}
            {estatisticas.total > 0 && (
              <button
                onClick={() => setMostrarEstoque(true)}
                className="button-primary-refined-full"
              >
                <Archive className="w-4 h-4" />
                <span className="sf-pro-text font-semibold">Gerenciar Estoque ({estatisticas.total})</span>
              </button>
            )}
          </div>
        </GlassCard>

        {/* Cards de Benefícios */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="benefit-card-compact">
            <div className="benefit-icon-compact bg-gradient-to-br from-blue-500 to-blue-600">
              <Eye className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="sf-pro-display font-semibold text-slate-900 text-sm mb-0.5">
                Visibilidade
              </h3>
              <p className="sf-pro-text text-slate-600 text-xs leading-relaxed">
                Apareça quando médicos prescrevem
              </p>
            </div>
          </div>

          <div className="benefit-card-compact">
            <div className="benefit-icon-compact bg-gradient-to-br from-emerald-500 to-emerald-600">
              <Users className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="sf-pro-display font-semibold text-slate-900 text-sm mb-0.5">
                Leads Qualificados
              </h3>
              <p className="sf-pro-text text-slate-600 text-xs leading-relaxed">
                Pacientes via QR Code da receita
              </p>
            </div>
          </div>

          <div className="benefit-card-compact">
            <div className="benefit-icon-compact bg-gradient-to-br from-purple-500 to-purple-600">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="sf-pro-display font-semibold text-slate-900 text-sm mb-0.5">
                Métricas
              </h3>
              <p className="sf-pro-text text-slate-600 text-xs leading-relaxed">
                Acompanhe conversões em tempo real
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Cadastro */}
      <ModalCadastrarProduto
        visivel={modalAberto}
        onFechar={() => setModalAberto(false)}
        onSalvar={handleSalvarProduto}
        carregando={loading.salvando}
        nomeFarmacia={dadosFarmacia.nome}
      />

      {/* Footer */}
      <footer className="footer-refined">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <img src="/logo.PNG" alt="Scriptus" className="h-3.5 w-auto opacity-60" />
            <span className="text-[10px] sf-pro-text text-slate-500">
              Scriptus Tecnologia em Saúde LTDA © 62.420.408/0001-70
            </span>
          </div>
        </div>
      </footer>

      {/* Notification */}
      {mensagem.texto && (
        <div className="fixed bottom-6 right-6 left-6 sm:left-auto z-50 max-w-sm">
          <div className={`notification-refined ${
            mensagem.tipo === 'error' ? 'notification-error-refined' : 'notification-success-refined'
          }`}>
            <div className="flex items-center gap-3">
              <div className="notification-icon-refined">
                {mensagem.tipo === 'error' ? (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                )}
              </div>
              <span className="sf-pro-text font-medium text-slate-900 text-xs flex-1">
                {mensagem.texto}
              </span>
              <button 
                onClick={() => setMensagem({ texto: '', tipo: 'success' })} 
                className="notification-close-refined"
              >
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{styles}</style>
    </div>
  );
}

// ============================================================================
// STYLES - GLASSMORPHISM PREMIUM REFINADO
// ============================================================================
const styles = `
  /* ===== TIPOGRAFIA ===== */
  .sf-pro-display {
    font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  .sf-pro-text {
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* ===== BACKGROUND ===== */
  .bg-gradient-refined {
    background: linear-gradient(135deg, 
      #f8fafc 0%, 
      #ffffff 25%, 
      #f1f5f9 50%, 
      #ffffff 75%, 
      #ecfdf5 100%);
  }

  .background-pattern-refined {
    position: absolute;
    inset: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 50% 10%, rgba(99, 102, 241, 0.04) 0%, transparent 60%);
    opacity: 0.7;
  }

  .background-glow-1 {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(40px);
  }

  .background-glow-2 {
    position: absolute;
    bottom: 15%;
    right: 20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(35px);
  }

  .background-glow-3 {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.02) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }

  /* ===== HEADER ===== */
  .header-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  }

  .header-scrolled-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  }

  .logo-link-refined {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .logo-link-refined:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }

  /* ===== GLASS CARDS ===== */
  .glass-card-refined {
    background: rgba(255, 255, 255, 0.80);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.06),
      0 4px 16px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .glass-card-elevated-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.08),
      0 12px 24px rgba(0, 0, 0, 0.06),
      0 6px 12px rgba(0, 0, 0, 0.04),
      inset 0 2px 0 rgba(255, 255, 255, 0.95);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* ===== ICONS ===== */
  .main-icon-refined {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 2px 0 rgba(255, 255, 255, 0.9);
    flex-shrink: 0;
  }

  .stat-icon-compact {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.1),
      0 2px 6px rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
  }

  .benefit-icon-compact {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 11px;
    box-shadow: 
      0 6px 16px rgba(0, 0, 0, 0.1),
      0 3px 8px rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
  }

  /* ===== STAT CARDS ===== */
  .stat-card-compact {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  .stat-card-compact:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  /* ===== BENEFIT CARDS ===== */
  .benefit-card-compact {
    background: rgba(255, 255, 255, 0.80);
    backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 14px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 6px 20px rgba(0, 0, 0, 0.05),
      0 3px 10px rgba(0, 0, 0, 0.03),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .benefit-card-compact:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 12px 32px rgba(0, 0, 0, 0.08),
      0 6px 16px rgba(0, 0, 0, 0.05);
    border-color: rgba(16, 185, 129, 0.3);
  }

  /* ===== BUTTONS ===== */
  .button-primary-refined {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 24px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    border-radius: 14px;
    color: white;
    font-weight: 700;
    font-size: 0.875rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 10px 32px rgba(16, 185, 129, 0.25),
      0 6px 16px rgba(16, 185, 129, 0.15),
      inset 0 2px 0 rgba(255, 255, 255, 0.25);
    cursor: pointer;
    text-decoration: none;
  }

  .button-primary-refined:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px);
    box-shadow: 
      0 16px 40px rgba(16, 185, 129, 0.3),
      0 8px 20px rgba(16, 185, 129, 0.2);
  }

  .button-primary-refined:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .button-primary-refined-full {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    width: 100%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    border-radius: 12px;
    color: white;
    font-weight: 700;
    font-size: 0.8rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 8px 24px rgba(16, 185, 129, 0.2),
      0 4px 12px rgba(16, 185, 129, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    cursor: pointer;
  }

  .button-primary-refined-full:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 
      0 12px 32px rgba(16, 185, 129, 0.25),
      0 6px 16px rgba(16, 185, 129, 0.15);
  }

  .button-primary-refined-full:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .button-secondary-compact {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 18px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(16px);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-radius: 11px;
    color: #475569;
    font-weight: 600;
    font-size: 0.8rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    cursor: pointer;
    text-decoration: none;
  }

  .button-secondary-compact:hover {
    background: rgba(255, 255, 255, 0.95);
    color: #1e293b;
    transform: translateY(-1px);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  /* ===== BADGES ===== */
  .badge-pharmacy-refined {
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.08));
    border: 1px solid rgba(16, 185, 129, 0.25);
    border-radius: 9px;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
  }

  /* ===== NOTIFICATIONS ===== */
  .notification-refined {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(24px) saturate(200%);
    -webkit-backdrop-filter: blur(24px) saturate(200%);
    border-radius: 14px;
    padding: 14px 18px;
    animation: slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .notification-success-refined {
    border: 1px solid rgba(16, 185, 129, 0.3);
    box-shadow: 
      0 20px 60px rgba(16, 185, 129, 0.15),
      0 10px 30px rgba(16, 185, 129, 0.1);
  }

  .notification-error-refined {
    border: 1px solid rgba(239, 68, 68, 0.3);
    box-shadow: 
      0 20px 60px rgba(239, 68, 68, 0.15),
      0 10px 30px rgba(239, 68, 68, 0.1);
  }

  .notification-icon-refined {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
  }

  .notification-close-refined {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 7px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .notification-close-refined:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  /* ===== FOOTER ===== */
  .footer-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    padding: 16px 0;
    margin-top: 56px;
  }

  /* ===== ANIMATIONS ===== */
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 768px) {
    .button-primary-refined-full {
      padding: 11px 16px;
      font-size: 0.75rem;
    }

    .main-icon-refined {
      width: 44px;
      height: 44px;
    }

    .benefit-icon-compact {
      width: 36px;
      height: 36px;
    }

    .stat-card-compact {
      padding: 10px;
    }

    .benefit-card-compact {
      padding: 14px;
    }
  }

  /* ===== ACCESSIBILITY ===== */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (prefers-contrast: high) {
    .glass-card-refined, 
    .glass-card-elevated-refined {
      background: rgba(255, 255, 255, 0.95);
      border: 2px solid rgba(0, 0, 0, 0.2);
    }
  }

  @media (pointer: coarse) {
    button, a, [role="button"] { 
      min-height: 44px; 
      min-width: 44px; 
    }
  }
`;